import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Filter query params ignored when deciding whether "a filter is active."
 * `filter.v.availability` is force-applied by layout/theme.liquid on every
 * collection page load with no query params, so it must not by itself
 * count as the shopper having adjusted a filter. Its absence does count:
 * the shopper removed "In stock" to see sold-out works, which the feed hides.
 */
const AVAILABILITY_FILTER_PARAM = 'filter.v.availability';
const IGNORED_FILTER_PARAMS = new Set([AVAILABILITY_FILTER_PARAM]);

/**
 * @param {URLSearchParams} queryParams
 * @returns {boolean}
 */
function hasMeaningfulActiveFilters(queryParams) {
  if (!queryParams.has(AVAILABILITY_FILTER_PARAM)) return true;
  for (const key of queryParams.keys()) {
    if (key.startsWith('filter.') && !IGNORED_FILTER_PARAMS.has(key)) return true;
  }
  return false;
}

/**
 * The currently desired view: 'all' (the regular product grid) or the key
 * of a feed panel ('feed' = e.g. "Top 40", 'feed-2' = e.g. "Books,
 * stickers & co"). Re-applied after every DOM mutation so it survives
 * facets.js's async section morph (see the MutationObserver below for why
 * this can't just be applied once on the triggering event).
 * @type {string}
 */
let currentView = 'feed';

/**
 * The view shown without any filter: the first feed, or 'all' when the
 * section has only the second feed.
 * @type {string}
 */
let defaultView = 'feed';

/**
 * Shows the current feed panel and hides the others and #ResultsList, or
 * shows #ResultsList alone for 'all'. Always
 * re-queries the live DOM rather than caching element references, since
 * facets.js morphs the whole main-collection section (tab, panel and
 * #ResultsList alike) on every filter change or Clear All.
 */
function applyCurrentView() {
  const showingFeed = currentView !== 'all';

  // blocks/filters.liquid renders the feed buttons twice — once for the
  // desktop pill row, once for the mobile toggle bar, each shown/hidden
  // purely via @media — so both copies must stay in sync.
  for (const tab of document.querySelectorAll('[data-pp-feed-target]')) {
    const active = tab instanceof HTMLElement && tab.dataset.ppFeedTarget === currentView;
    tab.classList.toggle('is-active', active);
    tab.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  for (const panel of document.querySelectorAll('[data-pp-feed-panel]')) {
    if (!(panel instanceof HTMLElement)) continue;
    panel.toggleAttribute('hidden', panel.dataset.ppFeedPanel !== currentView);
  }

  const allProductsPanel = document.querySelector('#ResultsList');
  if (allProductsPanel) {
    // .grid sets `display` directly with no [hidden] guard, so the
    // hidden attribute alone won't hide #ResultsList — the .hidden
    // utility class (display: none !important) is required instead.
    allProductsPanel.classList.toggle('hidden', showingFeed);
  }
}

/** @param {string} view */
function activateFeedView(view) {
  currentView = view;
  applyCurrentView();
}

/** @param {MouseEvent} event */
function handleDocumentClick(event) {
  const target = event.target instanceof HTMLElement ? event.target : null;
  const tab = target?.closest('[data-pp-feed-target]');
  if (!(tab instanceof HTMLElement)) return;
  event.preventDefault();
  const view = tab.dataset.ppFeedTarget ?? 'all';
  activateFeedView(currentView === view ? 'all' : view);
}

/**
 * Deliberately ignores event.detail.queryParams: FacetsFormComponent
 * builds it from the live form's FormData (assets/facets.js
 * createURLParameters), which for Clear All / individual-filter-removal
 * (updateFiltersByURL) hasn't been reset yet at dispatch time — so it can
 * still report the just-removed filter as active. history.pushState runs
 * before the event fires in both updateFilters and updateFiltersByURL,
 * so window.location.search is the reliable source of the params that
 * are about to be requested from the server.
 */
function handleFilterUpdate() {
  const queryParams = new URLSearchParams(window.location.search);
  activateFeedView(hasMeaningfulActiveFilters(queryParams) ? 'all' : defaultView);
}

/**
 * facets.js dispatches ThemeEvents.FilterUpdate synchronously and only
 * *afterwards* kicks off the section's async fetch + morph (see
 * FacetsFormComponent#updateFilters in assets/facets.js). By the time the
 * morph actually swaps in fresh server-rendered HTML — with no knowledge
 * of this client-side view state — any class we set in the event handler
 * has already been overwritten. Observing DOM mutations and re-applying
 * the current view after each one is what makes the state stick.
 */
let reapplyScheduled = false;
const sectionMutationObserver = new MutationObserver(() => {
  if (reapplyScheduled) return;
  reapplyScheduled = true;
  requestAnimationFrame(() => {
    reapplyScheduled = false;
    applyCurrentView();
  });
});

/**
 * Bound once at module scope rather than per custom-element instance:
 * facets.js can morph <pretty-portal-feed-selector> in place (unchanged
 * content) without ever calling disconnectedCallback, which would leave
 * instance-bound listeners with no matching teardown to rely on.
 */
if (!window.__prettyPortalFeedSelectorBound) {
  window.__prettyPortalFeedSelectorBound = true;
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener(ThemeEvents.FilterUpdate, handleFilterUpdate);

  // Scoped to the <results-list> section wrapper (the actual morph target
  // in assets/section-renderer.js) rather than document.body, since this
  // module only ever loads as a descendant of that element and observing
  // the whole document fires on unrelated mutations anywhere on the page
  // (cart drawer, search modal, other sections, theme editor overlays).
  const morphRoot = document.querySelector('results-list') ?? document.body;
  sectionMutationObserver.observe(morphRoot, { childList: true, subtree: true });
}

/**
 * Wraps the curated feed panels (e.g. "Top 40", "Books, stickers & co") on
 * the shop page. The feed buttons live inside the filters bar (rendered by blocks/filters.liquid
 * via the `feed_button` param passed from main-collection.liquid) — a
 * different DOM subtree than this element. All toggle behavior is handled
 * by the module-level listeners above; this class only sets the initial
 * view on first render.
 *
 * @extends {Component}
 */
class PrettyPortalFeedSelector extends Component {
  connectedCallback() {
    super.connectedCallback();
    defaultView = this.querySelector('[data-pp-feed-panel="feed"]') ? 'feed' : 'all';
    activateFeedView(this.dataset.initialView === 'all' ? 'all' : defaultView);
  }
}

if (!customElements.get('pretty-portal-feed-selector')) {
  customElements.define('pretty-portal-feed-selector', PrettyPortalFeedSelector);
}
